import React from "react";
import styles from "./styles.module.scss";
import { useViewport } from "../ViewportProvider";
import { format, formatDuration, intervalToDuration } from "date-fns";

function formatTimeOutside(totalMinutes) {
  // 1. Create a duration object based on the minutes
  // We can do this by creating a date at 0 and a date after our total minutes
  const start = new Date(0);
  const end = new Date(totalMinutes * 60 * 1000); // convert to milliseconds

  const duration = intervalToDuration({ start, end });

  // 2. Decide which units to display based on your rules
  let format = ["minutes"]; // Default

  if (totalMinutes >= 1440) {
    // 24 hours or more
    format = ["days", "hours"];
  } else if (totalMinutes >= 60) {
    // 60 minutes to 23h 59m
    format = ["hours", "minutes"];
  }

  // 3. Format into a human-readable string
  return formatDuration(duration, {
    format,
    // Optional: Customize how the units look (e.g., "1 day 2 hrs")
    locale: {
      formatDistance: (token, count) => {
        const units = {
          xDays: count === 1 ? "day" : "days",
          xHours: count === 1 ? "hr" : "hrs",
          xMinutes: count === 1 ? "min" : "mins",
        };
        return `${count} ${units[token] || token}`;
      },
    },
  });
}

const OutsideTooltip = ({ outsideData }) => {
  return (
    <div>
      <div className={`${styles.tooltip_time} ${styles.tooltip_time_outside}`}>
        <div className={styles.tooltip_top_title}>Time outside</div>
        <div className={styles.tooltip_time_right}>
          {format(new Date(outsideData.originalStart), "HH:mm")} -{" "}
          {format(new Date(outsideData.originalEnd), "HH:mm")}
        </div>
      </div>
      <div
        className={`${styles.tooltip_time} ${styles.tooltip_time_outside} ${styles.tooltip_bottom}`}
      >
        <div className={styles.tooltip_top_title}>Total outside</div>
        <div className={styles.tooltip_time_right}>
          {formatTimeOutside(outsideData.durationMinutes)}
        </div>
      </div>
    </div>
  );
};
// Seperate component for the frontdoor tooltip as it splits events into door open and door closed
const FrontDoorTooltip = ({ events, outsideData }) => {
  const sortedEvents = [...events].sort((a, b) => a.time - b.time);
  return (
    <div>
      {sortedEvents.map((event, index) => (
        <div
          className={`${styles.tooltip_time} ${
            index % 2 === 0 && styles.tooltip_time_even
          }`}
          key={index}
        >
          <div className={styles.tooltip_time_right}>
            {event.status === "opened" ? "Door open" : "Door close"}
          </div>
          <div>{format(new Date(event.time), "HH:mm")}</div>
        </div>
      ))}
      {outsideData && (
        <div
          className={`${styles.tooltip_time} ${styles.tooltip_time_outside} ${styles.tooltip_bottom}`}
          key={`${outsideData.time}-${outsideData.label}`}
        >
          <div className={styles.tooltip_top_title}>Time outside</div>
          <div className={styles.tooltip_time_right}>
            {format(new Date(outsideData.originalStart), "HH:mm")} -{" "}
            {format(new Date(outsideData.originalEnd), "HH:mm")}
          </div>
        </div>
      )}
    </div>
  );
};

const EventLogTooltip = ({
  entranceDoor = false,
  tooltipEvents,
  outsideState,
  outsideData,
  coordinatesSquare,
  time,
}) => {
  const { height, width } = useViewport();
  // Where there isn't enough screen space to have the tooltip aligned with the left hand side of the square we should align it with the right
  const rightAlign = width - coordinatesSquare.left < 168;
  return (
    <div
      className={styles.tooltip}
      style={{
        bottom: height - coordinatesSquare?.top + 2,
        left: rightAlign
          ? // take away the width of the tooltip and add the width of the square to right align
            coordinatesSquare?.left - 158 + 29
          : coordinatesSquare?.left,
      }}
    >
      {outsideState !== "outside" && (
        <div className={styles.tooltip_top}>
          <div className={styles.tooltip_top_title}>Event time(s)</div>
          <div className={styles.tooltip_top_times}>
            {time.startTime} - {time.endTime}
          </div>
        </div>
      )}
      {entranceDoor && outsideState !== "outside" && (
        <FrontDoorTooltip events={tooltipEvents} outsideData={outsideData} />
      )}
      {outsideState === "outside" && (
        <OutsideTooltip outsideData={outsideData} />
      )}
      {!entranceDoor && (
        <div>
          {tooltipEvents.map((event, index) => (
            <div
              className={`${styles.tooltip_time} ${
                index % 2 === 0 && styles.tooltip_time_even
              }`}
              key={index}
            >
              <div>{format(new Date(event), "HH:mm")}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventLogTooltip;
